import React, { useEffect, useState } from "react";
import { Table, Breadcrumb, Button, Empty, Modal, message, Tooltip } from "antd";
import { Link, useNavigate } from "react-router-dom";
import { PlusCircleOutlined, DeleteOutlined, EyeOutlined } from "@ant-design/icons";
import { RefreshCcw } from "lucide-react";
import { v4 as uuidv4 } from "uuid";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  fetchRctExperiments,
  fetchRctExperimentRuns,
  deleteRctExperiment,
  RctExperimentSummary,
  RctExperimentRun,
} from "./components/rctExperimentApi";

dayjs.extend(utc);

const rctBreadCrumbs = [
  {
    name: "Home",
    url: "/",
  },
  {
    name: "RCT Experiments",
    url: "/rct-experiment/landing",
  },
  {
    name: "Past Tests",
    url: "/rct-experiment/list",
  },
];

interface RctExperimentRecord {
  key: string;
  name: string;
  createdDate: string;
  status: string;
}

// Mirrors GeoLift's getJobStatus (geo-lift/index.tsx): match the config to
// its job run(s) and use the run's real status, not the config's own
// largely-unused `status` field. A run stuck RUNNING/PENDING for 2+ hours is
// treated as FAILED, same staleness rule GeoLift uses.
const getJobStatus = (jobConfigId: string, jobRunList: RctExperimentRun[]): string => {
  const run = jobRunList.find((r) => r.jobConfigId === jobConfigId);
  if (!run) return "UNKNOWN";
  const isStuck =
    (run.jobStatus === "RUNNING" || run.jobStatus === "PENDING") &&
    dayjs.utc().diff(dayjs.utc(run.jobExecutionTime), "hour") >= 2;
  return isStuck ? "FAILED" : run.jobStatus;
};

const RctExperimentList: React.FC = () => {
  const navigate = useNavigate();
  const [experiments, setExperiments] = useState<RctExperimentSummary[]>([]);
  const [runs, setRuns] = useState<RctExperimentRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [deletingJobs, setDeletingJobs] = useState<{ [key: string]: boolean }>({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [configs, jobRuns] = await Promise.all([fetchRctExperiments(), fetchRctExperimentRuns()]);
      setExperiments(configs);
      setRuns(jobRuns);
    } catch (err) {
      console.error("[RctExperiment] Failed to load experiments:", err);
      message.error("Failed to load RCT experiments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleView = (jobConfigId: string, name: string) => {
    navigate(`/rct-experiment/${jobConfigId}?view=true&name=${encodeURIComponent(name)}`);
  };

  const handleDelete = (jobConfigId: string, name: string) => {
    Modal.confirm({
      title: "Delete RCT Experiment",
      content: `Are you sure you want to delete "${name}"? This action cannot be undone.`,
      okText: "Delete",
      okType: "danger",
      cancelText: "Cancel",
      onOk: async () => {
        try {
          setDeletingJobs((prev) => ({ ...prev, [jobConfigId]: true }));
          await deleteRctExperiment(jobConfigId);
          message.success(`Successfully deleted "${name}"`);
          await fetchData();
        } catch (err: any) {
          console.error("[RctExperiment] Failed to delete experiment:", err);
          message.error(err?.response?.data?.detail || "Failed to delete this experiment. Please try again.");
        } finally {
          setDeletingJobs((prev) => ({ ...prev, [jobConfigId]: false }));
        }
      },
    });
  };

  const records: RctExperimentRecord[] = [...experiments]
    .sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
    .map((e) => ({
      key: e.jobConfigId,
      name: e.name,
      createdDate: e.createdDate,
      status: getJobStatus(e.jobConfigId, runs),
    }));

  return (
    <div className="h-full overflow-y-auto p-5">
      <div className="mb-6 flex justify-between items-center">
        <Breadcrumb>
          {rctBreadCrumbs.map((entity, index) => (
            <Breadcrumb.Item key={index}>
              <Link to={entity.url}>{entity.name}</Link>
            </Breadcrumb.Item>
          ))}
        </Breadcrumb>
        <div className="flex items-center gap-2 justify-end">
          <Link to={`/rct-experiment/${uuidv4()}`}>
            <Button
              type="primary"
              icon={<PlusCircleOutlined className="h-4 w-4" />}
              className="flex items-center gap-2"
            >
              Run RCT Experiment
            </Button>
          </Link>
          <Button icon={<RefreshCcw className="h-4 w-4" />} onClick={fetchData} loading={loading}>
            Refresh
          </Button>
        </div>
      </div>

      <Table
        columns={[
          {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: (a, b) => a.name.localeCompare(b.name),
            render: (text: string) => (
              <span className="text-blue-600 hover:text-blue-800">{text}</span>
            ),
          },
          {
            title: "Created Date",
            dataIndex: "createdDate",
            key: "createdDate",
            render: (date) => dayjs.utc(`${date}Z`).local().format("MMM D, YYYY HH:mm"),
            sorter: (a, b) =>
              dayjs.utc(`${a.createdDate}Z`).unix() - dayjs.utc(`${b.createdDate}Z`).unix(),
          },
          {
            title: "Action",
            dataIndex: "status",
            key: "action",
            filters: [
              { text: "Running", value: "RUNNING" },
              { text: "In Queue", value: "PENDING" },
              { text: "Failed", value: "FAILED" },
              { text: "Successful", value: "SUCCESSFUL" },
              { text: "Not started", value: "UNKNOWN" },
            ],
            onFilter: (value, record) => record.status === value,
            render: (status, record) => {
              const isDeleting = deletingJobs[record.key];
              const deleteButton = (
                <Tooltip title="Delete experiment">
                  <Button
                    type="link"
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record.key, record.name)}
                    loading={isDeleting}
                    disabled={isDeleting}
                  >
                    Delete
                  </Button>
                </Tooltip>
              );
              switch (status) {
                case "RUNNING":
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary border-t-transparent"></div>
                        <span>Running</span>
                      </div>
                      {deleteButton}
                    </div>
                  );
                case "PENDING":
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="flex items-center gap-2">
                        <div className="flex gap-1">
                          <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                          <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                          <div className="w-1.5 h-1.5 bg-primary rounded-full animate-bounce"></div>
                        </div>
                        <span>In Queue</span>
                      </div>
                      {deleteButton}
                    </div>
                  );
                case "FAILED":
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-red-600 text-sm font-medium">Failed</span>
                      {deleteButton}
                    </div>
                  );
                case "SUCCESSFUL":
                  return (
                    <div className="flex items-center gap-2 flex-nowrap">
                      <Button
                        type="primary"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => handleView(record.key, record.name)}
                      >
                        View
                      </Button>
                      {deleteButton}
                    </div>
                  );
                default:
                  return (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-gray-400 text-sm">Not started</span>
                      {deleteButton}
                    </div>
                  );
              }
            },
          },
        ]}
        dataSource={records}
        loading={loading}
        rowKey="key"
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="No RCT experiments yet"
            />
          ),
        }}
        pagination={{
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
          pageSizeOptions: ["10", "20", "50", "100"],
          position: ["bottomRight"],
          className: "mt-4",
        }}
      />
    </div>
  );
};

export default RctExperimentList;
